import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import OnboardingLayout from '../../components/layouts/onboarding/OnboardingLayout';
import { debounce } from '../../utils/debounce';
import storage from '../../utils/storage';
import {
  Form,
  FormContainer,
  FormFields,
  ButtonContainer,
  CompanyDisplay,
  CompanyValue,
  AutocompleteDropdown,
  AutocompleteItem
} from '../../styles/components';
import InputField from '../../components/common/InputField';
import PrimaryButton from '../../components/common/PrimaryButton';
import { colors, typography } from '../../styles/theme/index';
import { industries } from '../../data/industries';
import { industries as beautyIndustries } from '../../data/industries_beauty';

const OnboardingStep3 = () => {
  const navigate = useNavigate();
  const [OrganisationType, setOrganisationType] = useState('');
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    subIndustry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [filteredIndustries, setFilteredIndustries] = useState([]);
  const [filteredSubIndustries, setFilteredSubIndustries] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const dropdownRef = useRef(null);
  const subDropdownRef = useRef(null);
  const companyDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setFilteredIndustries([]);
      }
      if (subDropdownRef.current && !subDropdownRef.current.contains(event.target)) {
        setFilteredSubIndustries([]);
      }
      if (companyDropdownRef.current && !companyDropdownRef.current.contains(event.target)) {
        setFilteredCompanies([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load organization type
  const loadOrganisationType = useCallback(() => {
    const storedOrganisationType = storage.getItem('selectedOrganisationType');
    if (storedOrganisationType) {
      const typeObject = {
        'sole-trader': 'Sole Trader',
        'limited-company': 'Ltd Company',
        'enterprise': 'Enterprise',
        'charity': 'Charity / NGO'
      };
      const displayType = typeObject[storedOrganisationType] || '';
      setOrganisationType(displayType);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    // Check for required data from previous steps
    const pipedriveData = storage.getPipedriveData();
    const orgType = storage.getItem('selectedOrganisationType');
    
    if (!pipedriveData?.personId || !pipedriveData?.dealId || !orgType) {
      // If missing organization type, go back to step 2
      // If missing Pipedrive data, go back to step 1
      navigate(orgType ? '/onboarding/step-one' : '/onboarding/step-two');
      return;
    }

    // Load previously saved data if it exists
    const savedData = storage.getItem('step3Data');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      setFormData(parsedData);
    }

    // Load organization type
    loadOrganisationType();
  }, [navigate, loadOrganisationType]);

  // Original Companies House search functionality
  const handleCompanySearch = async (value) => {
    // Skip Companies House search for sole traders
    const orgType = storage.getItem('selectedOrganisationType');
    if (orgType === 'sole-trader') {
      return;
    }

    if (value.length < 3) {
      setFilteredCompanies([]);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/companies/search?q=${encodeURIComponent(value)}`);
      const result = await response.json();
      
      if (result.success && result.data && result.data.items) {
        setFilteredCompanies(result.data.items.map(item => ({
          title: item.title,
          company_number: item.company_number,
          address_snippet: item.address_snippet
        })));
      } else {
        setFilteredCompanies([]);
      }
    } catch (error) {
      console.error('Error searching companies:', error);
      setFilteredCompanies([]);
    }
  };

  const debouncedCompanySearch = useCallback(
    debounce(handleCompanySearch, 300),
    []
  );

  const handleCompanyNameChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      companyName: value
    }));
    debouncedCompanySearch(value);
  };

  const handleCompanySelect = (company) => {
    setFormData(prev => ({
      ...prev,
      companyName: company.title || company.name,
      companyNumber: company.company_number || company.number,
      companyAddress: company.address_snippet || company.address
    }));
    setFilteredCompanies([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Get stored Pipedrive data
      const pipedriveData = storage.getPipedriveData();
      if (!pipedriveData) {
        throw new Error('No Pipedrive data found');
      }

      // Get organization type
      const storedOrgType = storage.getItem('selectedOrganisationType');
      if (!storedOrgType) {
        throw new Error('Organization type not found');
      }

      // Send data to backend
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/onboarding/step-three`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          personId: pipedriveData.personId,
          dealId: pipedriveData.dealId,
          organizationType: storedOrgType
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.error || 'Failed to process step 3');
      }

      // Store the organization ID
      storage.setPipedriveData(
        pipedriveData.personId,
        pipedriveData.dealId,
        result.data.organizationId
      );
      
      // Store form data
      storage.setItem('step3Data', JSON.stringify(formData));

      // Navigate to next step
      navigate('/onboarding/step-four');
    } catch (error) {
      console.error('Form submission error:', error);
      setError(error.message || 'There was an error processing your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBack = () => {
    // Store current data
    storage.setItem('step3Data', JSON.stringify({ ...formData, OrganisationType }));
    
    // Get step1Data to preserve it
    const step1Data = JSON.parse(storage.getItem('step1Data') || '{}');
    
    // Navigate back
    navigate('/onboarding/step-two');
    
    // Restore step1Data after navigation
    if (step1Data) {
      storage.setItem('step1Data', JSON.stringify(step1Data));
    }
  };

  const handleIndustryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      industry: value,
      subIndustry: '' // Reset sub-industry when industry changes
    }));
    
    const filtered = industries.filter(industry => 
      industry.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredIndustries(filtered);
  };

  const handleSubIndustryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      subIndustry: value
    }));
    
    const filtered = beautyIndustries.filter(industry => 
      industry.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredSubIndustries(filtered);
  };

  const handleIndustrySelect = (industry) => {
    setFormData(prev => ({
      ...prev,
      industry,
      subIndustry: '' // Reset sub-industry when industry changes
    }));
    setFilteredIndustries([]);
  };

  const handleSubIndustrySelect = (subIndustry) => {
    setFormData(prev => ({
      ...prev,
      subIndustry
    }));
    setFilteredSubIndustries([]);
  };

  const showSubIndustry = formData.industry === 'Beauty Therapy';

  return (
    <OnboardingLayout
      currentStep={3}
      title="Tell us about your company"
      subtitle="This helps us understand your business better"
      onBack={handleBack}
    >
      <FormContainer>
        <CompanyDisplay>
          <CompanyValue>{OrganisationType}</CompanyValue>
        </CompanyDisplay>
        <Form onSubmit={handleSubmit}>
          {error && (
            <div style={{ color: 'red', marginBottom: '1rem' }}>
              {error}
            </div>
          )}
          <FormFields>
            {localStorage.getItem('selectedOrganisationType') !== 'sole-trader' ? (
              <div ref={companyDropdownRef} style={{ position: 'relative' }}>
                <InputField
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleCompanyNameChange}
                  placeholder="Start typing to search..."
                  required
                />
                {filteredCompanies.length > 0 && (
                  <AutocompleteDropdown>
                    {filteredCompanies.map((company) => (
                      <AutocompleteItem
                        key={company.company_number}
                        onClick={() => handleCompanySelect(company)}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ 
                            fontWeight: typography.fontWeight.bold,
                            color: colors.text.primary 
                          }}>
                            {company.title}
                          </span>
                          <div style={{ 
                            display: 'flex', 
                            gap: '8px',
                            fontSize: typography.fontSize.sm,
                            color: colors.text.secondary
                          }}>
                            {company.address_snippet && (
                              <span style={{ lineHeight: '1.2' }}>{company.address_snippet}</span>
                            )}
                            
                          </div>
                        </div>
                      </AutocompleteItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </div>
            ) : (
              <InputField
                label="Business Name"
                name="companyName"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder="Enter your business name"
                required
              />
            )}
            <div style={{ position: 'relative' }}>
              <InputField
                label="Industry"
                name="industry"
                value={formData.industry}
                onChange={handleIndustryChange}
                required
              />
              {filteredIndustries.length > 0 && (
                <AutocompleteDropdown ref={dropdownRef}>
                  {filteredIndustries.map((industry, index) => (
                    <AutocompleteItem
                      key={index}
                      onClick={() => handleIndustrySelect(industry)}
                    >
                      {industry}
                    </AutocompleteItem>
                  ))}
                </AutocompleteDropdown>
              )}
            </div>
            {showSubIndustry && (
              <div style={{ position: 'relative' }}>
                <InputField
                  label="Sub Industry"
                  name="subIndustry"
                  value={formData.subIndustry}
                  onChange={handleSubIndustryChange}
                  required
                />
                {filteredSubIndustries.length > 0 && (
                  <AutocompleteDropdown ref={subDropdownRef}>
                    {filteredSubIndustries.map((industry, index) => (
                      <AutocompleteItem
                        key={index}
                        onClick={() => handleSubIndustrySelect(industry)}
                      >
                        {industry}
                      </AutocompleteItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </div>
            )}
          </FormFields>
          <ButtonContainer>
            <PrimaryButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Processing...' : 'Continue'}
            </PrimaryButton>
          </ButtonContainer>
        </Form>
      </FormContainer>
    </OnboardingLayout>
  );
};

export default OnboardingStep3; 